import type { TemplateCategory } from "./templates.js";
import type { ListEntry, LocalNames } from "./team.js";

/**
 * Industry packs (行业包): data that tailors a salon's DEFAULTS and WORDING to its kind of business. The salon picks
 * one or more business types at signup (primary first, tenant.business_types); everything below is derived from them:
 *  - staff level presets and performance role presets (applied once, when the salon is created)
 *  - job-title suggestions, service starter templates, product/consumable starter categories
 *  - the staff noun used across the UI (zh 服务人员 by default; 技师 / 发型师 / 美甲师 … per industry)
 *  - default website template + gallery filter, booking-page wording, schema.org business type (JSON-LD)
 *  - package / membership examples and sample WhatsApp wording
 *
 * Packs are data: add an industry by adding an entry here (the web app renders the picture cards from this registry).
 * Changing a salon's industry later only changes defaults and wording, never existing data.
 */
export const INDUSTRY_REGISTRY_VERSION = 1;

export type L3 = { en: string; zh: string; ms: string };
export const INDUSTRY_KEYS = ["hair", "barber", "beauty", "nails", "massage", "spa", "clinic", "other"] as const;
export type Industry = (typeof INDUSTRY_KEYS)[number];
export const isIndustry = (x: unknown): x is Industry => typeof x === "string" && (INDUSTRY_KEYS as readonly string[]).includes(x);

export interface PresetEntry { code: string; names: L3 }
export interface IndustryPack {
  key: Industry;
  /** bump when the pack's presets change */
  version: number;
  name: L3;
  /** emoji shown on the picture cards */
  icon: string;
  /** card artwork colours (gradient) */
  colors: [string, string];
  staffNoun: L3;
  levels: PresetEntry[];
  defaultLevel: string;
  /** extra performance roles on top of the common Lead / Assistant / Sales */
  extraRoles: PresetEntry[];
  jobTitles: L3[];
  serviceTemplates: TemplateCategory[];
  productCategories: L3[];
  /** website template key (apps/api/src/site/templates.ts) and the gallery filter (website business type, or "all") */
  websiteTemplate: string;
  galleryFilter: string;
  /** schema.org type for the website JSON-LD */
  schemaType: string;
  /** booking page headline */
  bookTitle: L3;
  packageExamples: L3[];
  whatsappSample: L3;
}

const l3 = (en: string, zh: string, ms: string): L3 => ({ en, zh, ms });
const e = (code: string, en: string, zh: string, ms: string): PresetEntry => ({ code, names: l3(en, zh, ms) });

/** Performance roles every salon starts with (line-type defaults: service → lead, product/top-up/package → sales). */
export const COMMON_ROLES: PresetEntry[] = [
  e("primary", "Lead", "主理", "Utama"),
  e("assistant", "Assistant", "助理", "Pembantu"),
  e("seller", "Sales", "销售", "Jualan"),
];
const CONSULTANT = e("consultant", "Consultant", "顾问", "Perunding");
const REFERRER = e("referrer", "Referrer", "推荐人", "Perujuk");
/** Roles offered as one-click additions in Team → Levels & roles. */
export const SUGGESTED_ROLES: PresetEntry[] = [CONSULTANT, REFERRER];

/** Generic level ladder (used for "other" and as the neutral default). */
export const GENERIC_LEVELS: PresetEntry[] = [
  e("junior", "Junior", "初级", "Junior"),
  e("intermediate", "Intermediate", "中级", "Pertengahan"),
  e("senior", "Senior", "高级", "Kanan"),
  e("director", "Director", "总监", "Pengarah"),
  e("principal", "Principal", "首席", "Prinsipal"),
];

const RECEPTION = l3("Receptionist", "前台", "Penyambut tetamu");
const MANAGER = l3("Store manager", "店长", "Pengurus kedai");

export const INDUSTRY_PACKS: Record<Industry, IndustryPack> = {
  hair: {
    key: "hair", version: 1, name: l3("Hair salon", "发廊", "Salun rambut"), icon: "💇", colors: ["#8a5a2b", "#e9c9a0"],
    staffNoun: l3("Stylist", "发型师", "Pendandan"),
    levels: [e("assistant", "Assistant", "助理", "Pembantu"), e("stylist", "Stylist", "发型师", "Pendandan"), e("senior_stylist", "Senior stylist", "资深发型师", "Pendandan kanan"), e("director", "Director", "总监", "Pengarah")],
    defaultLevel: "stylist", extraRoles: [],
    jobTitles: [l3("Stylist", "发型师", "Pendandan"), l3("Colourist", "染发师", "Pakar warna"), l3("Assistant", "助理", "Pembantu"), RECEPTION, MANAGER],
    serviceTemplates: ["hair"],
    productCategories: [l3("Shampoo & care", "洗护", "Syampu & penjagaan"), l3("Colour", "染膏", "Pewarna"), l3("Styling", "造型品", "Penggayaan"), l3("Tools", "工具", "Peralatan")],
    websiteTemplate: "elegant", galleryFilter: "hair", schemaType: "HairSalon",
    bookTitle: l3("Book your hair appointment", "预约发型服务", "Tempah temujanji rambut"),
    packageExamples: [l3("5 × wash & blow dry", "洗吹 5 次卡", "5 × cuci & keringkan"), l3("Colour care package", "染后护理套餐", "Pakej penjagaan warna")],
    whatsappSample: l3("Hi {name}, time for a refresh? Book your next cut this week and get a free treatment.", "{name} 您好，该修剪头发啦！本周预约即送护理一次。", "Hai {name}, masa untuk gaya baharu? Tempah minggu ini dan dapatkan rawatan percuma."),
  },
  barber: {
    key: "barber", version: 1, name: l3("Barber", "理发", "Kedai gunting"), icon: "💈", colors: ["#1b263b", "#c81e1e"],
    staffNoun: l3("Barber", "理发师", "Tukang gunting"),
    levels: [e("apprentice", "Apprentice", "学徒", "Perantis"), e("barber", "Barber", "理发师", "Tukang gunting"), e("senior_barber", "Senior barber", "资深理发师", "Tukang gunting kanan"), e("master_barber", "Master barber", "首席理发师", "Tukang gunting utama")],
    defaultLevel: "barber", extraRoles: [],
    jobTitles: [l3("Barber", "理发师", "Tukang gunting"), l3("Apprentice", "学徒", "Perantis"), RECEPTION, MANAGER],
    serviceTemplates: ["barber"],
    productCategories: [l3("Grooming", "男士护理", "Dandanan"), l3("Beard care", "胡须护理", "Penjagaan janggut"), l3("Styling", "造型品", "Penggayaan"), l3("Blades & tools", "刀片与工具", "Pisau & peralatan")],
    websiteTemplate: "barber", galleryFilter: "barber", schemaType: "HairSalon",
    bookTitle: l3("Book your cut", "预约理发", "Tempah potongan anda"),
    packageExamples: [l3("4 cuts a month", "每月 4 次理发卡", "4 potongan sebulan"), l3("Cut & beard membership", "理发+修胡会员", "Keahlian gunting & janggut")],
    whatsappSample: l3("Hi {name}, looking sharp is a habit. Book your next cut and beard trim this week.", "{name} 您好，保持帅气从定期修剪开始，本周来剪个头吧！", "Hai {name}, sentiasa kemas! Tempah potongan dan rapi janggut minggu ini."),
  },
  beauty: {
    key: "beauty", version: 1, name: l3("Beauty & facial", "美容·护肤", "Kecantikan & rawatan muka"), icon: "🧖", colors: ["#b76e79", "#f6d5dc"],
    staffNoun: l3("Beautician", "美容师", "Jurukecantikan"),
    levels: [e("beautician", "Beautician", "美容师", "Jurukecantikan"), e("senior_beautician", "Senior beautician", "资深美容师", "Jurukecantikan kanan"), e("beauty_consultant", "Beauty consultant", "美容顾问", "Perunding kecantikan")],
    defaultLevel: "beautician", extraRoles: [CONSULTANT],
    jobTitles: [l3("Beautician", "美容师", "Jurukecantikan"), l3("Beauty consultant", "美容顾问", "Perunding kecantikan"), l3("Lash artist", "美睫师", "Artis bulu mata"), RECEPTION, MANAGER],
    serviceTemplates: ["beauty"],
    productCategories: [l3("Skincare", "护肤品", "Penjagaan kulit"), l3("Masks", "面膜", "Topeng muka"), l3("Lash & brow", "睫毛与眉", "Bulu mata & kening"), l3("Waxing", "脱毛蜡", "Waks")],
    websiteTemplate: "pastel", galleryFilter: "beauty", schemaType: "BeautySalon",
    bookTitle: l3("Book your treatment", "预约护理", "Tempah rawatan anda"),
    packageExamples: [l3("10 × signature facial", "招牌面部护理 10 次卡", "10 × rawatan muka istimewa"), l3("Glow membership (monthly)", "焕肤月度会员", "Keahlian seri (bulanan)")],
    whatsappSample: l3("Hi {name}, your skin deserves some care. Book a facial this month and get 15% off.", "{name} 您好，本月预约面部护理享 85 折，给肌肤一点呵护吧！", "Hai {name}, kulit anda perlukan penjagaan. Tempah rawatan muka bulan ini dan nikmati diskaun 15%."),
  },
  nails: {
    key: "nails", version: 1, name: l3("Nails & lashes", "美甲·美睫", "Kuku & bulu mata"), icon: "💅", colors: ["#7c3aed", "#f9a8d4"],
    staffNoun: l3("Nail technician", "美甲师", "Juruteknik kuku"),
    levels: [e("junior_nail_tech", "Junior nail technician", "初级美甲师", "Juruteknik kuku junior"), e("nail_tech", "Nail technician", "美甲师", "Juruteknik kuku"), e("senior_nail_tech", "Senior nail technician", "资深美甲师", "Juruteknik kuku kanan")],
    defaultLevel: "nail_tech", extraRoles: [],
    jobTitles: [l3("Nail technician", "美甲师", "Juruteknik kuku"), l3("Lash artist", "美睫师", "Artis bulu mata"), RECEPTION, MANAGER],
    serviceTemplates: ["nails"],
    productCategories: [l3("Gel polish", "甲油胶", "Pengilat gel"), l3("Nail care", "指甲护理", "Penjagaan kuku"), l3("Nail art", "美甲饰品", "Seni kuku"), l3("Tools", "工具", "Peralatan")],
    websiteTemplate: "nails", galleryFilter: "nails", schemaType: "NailSalon",
    bookTitle: l3("Book your nails", "预约美甲", "Tempah rawatan kuku"),
    packageExamples: [l3("5 × gel manicure", "光疗美甲 5 次卡", "5 × manikur gel"), l3("Mani + pedi monthly", "手足护理月卡", "Mani + pedi bulanan")],
    whatsappSample: l3("Hi {name}, new season colours are in! Book your gel manicure this week.", "{name} 您好，新一季甲色已上架，本周来做个光疗美甲吧！", "Hai {name}, warna musim baharu telah tiba! Tempah manikur gel minggu ini."),
  },
  massage: {
    key: "massage", version: 1, name: l3("Massage & reflexology", "按摩·足疗", "Urutan & refleksologi"), icon: "💆", colors: ["#3a4a3f", "#c8b98f"],
    staffNoun: l3("Therapist", "技师", "Terapis"),
    levels: [e("apprentice", "Apprentice", "学徒", "Perantis"), e("therapist", "Therapist", "技师", "Terapis"), e("senior_therapist", "Senior therapist", "高级技师", "Terapis kanan"), e("expert_therapist", "Expert therapist", "资深技师", "Terapis pakar")],
    defaultLevel: "therapist", extraRoles: [],
    jobTitles: [l3("Massage therapist", "按摩技师", "Terapis urut"), l3("Reflexologist", "足疗师", "Pakar refleksologi"), RECEPTION, MANAGER],
    serviceTemplates: ["massage"],
    productCategories: [l3("Massage oils", "按摩油", "Minyak urut"), l3("Balms", "药膏", "Balsem"), l3("Towels & linens", "毛巾布草", "Tuala & linen"), l3("Aromatherapy", "香薰", "Aromaterapi")],
    websiteTemplate: "zen", galleryFilter: "spa", schemaType: "HealthAndBeautyBusiness",
    bookTitle: l3("Book a massage", "预约按摩", "Tempah urutan"),
    packageExamples: [l3("10 × 60-min foot reflexology", "60 分钟足疗 10 次卡", "10 × refleksologi kaki 60 min"), l3("Stored value 500 + 80 bonus", "储值 500 送 80", "Nilai tersimpan 500 + bonus 80")],
    whatsappSample: l3("Hi {name}, tired after a long week? Book a 90-min massage this weekend and relax.", "{name} 您好，忙了一周辛苦了！周末来做个 90 分钟按摩放松一下吧。", "Hai {name}, penat seminggu? Tempah urutan 90 minit hujung minggu ini."),
  },
  spa: {
    key: "spa", version: 1, name: l3("Spa", "Spa 水疗", "Spa"), icon: "🌿", colors: ["#5b6b4e", "#e6dcc8"],
    staffNoun: l3("Therapist", "理疗师", "Terapis"),
    levels: [e("junior_therapist", "Junior therapist", "初级理疗师", "Terapis junior"), e("spa_therapist", "Spa therapist", "理疗师", "Terapis spa"), e("senior_spa_therapist", "Senior spa therapist", "资深理疗师", "Terapis spa kanan"), e("lead_therapist", "Lead therapist", "首席理疗师", "Ketua terapis")],
    defaultLevel: "spa_therapist", extraRoles: [CONSULTANT],
    jobTitles: [l3("Spa therapist", "理疗师", "Terapis spa"), l3("Spa consultant", "Spa 顾问", "Perunding spa"), RECEPTION, MANAGER],
    serviceTemplates: ["spa", "massage"],
    productCategories: [l3("Body oils", "身体精油", "Minyak badan"), l3("Scrubs & wraps", "磨砂与裹敷", "Lulur & balutan"), l3("Linens", "布草", "Linen"), l3("Aromatherapy", "香薰", "Aromaterapi")],
    websiteTemplate: "spa", galleryFilter: "spa", schemaType: "DaySpa",
    bookTitle: l3("Book your spa time", "预约 Spa 时光", "Tempah masa spa anda"),
    packageExamples: [l3("Couple spa day", "双人 Spa 日", "Hari spa pasangan"), l3("6 × aromatherapy massage", "香薰按摩 6 次卡", "6 × urutan aromaterapi")],
    whatsappSample: l3("Hi {name}, treat yourself to a spa afternoon. Book this week and enjoy a free foot soak.", "{name} 您好，给自己一个 Spa 下午吧！本周预约赠送泡脚一次。", "Hai {name}, manjakan diri dengan petang spa. Tempah minggu ini untuk rendaman kaki percuma."),
  },
  clinic: {
    key: "clinic", version: 1, name: l3("Aesthetics clinic", "医美", "Klinik estetik"), icon: "🩺", colors: ["#0f766e", "#cdeaf0"],
    staffNoun: l3("Practitioner", "治疗师", "Pengamal"),
    levels: [e("nurse", "Nurse", "护士", "Jururawat"), e("clinic_therapist", "Therapist", "治疗师", "Terapis"), e("doctor", "Doctor", "医生", "Doktor")],
    defaultLevel: "clinic_therapist", extraRoles: [CONSULTANT],
    jobTitles: [l3("Doctor", "医生", "Doktor"), l3("Nurse", "护士", "Jururawat"), l3("Therapist", "治疗师", "Terapis"), l3("Patient consultant", "客户顾问", "Perunding pesakit"), RECEPTION],
    serviceTemplates: ["clinic"],
    productCategories: [l3("Skincare", "护肤品", "Penjagaan kulit"), l3("Post-treatment care", "术后护理", "Penjagaan selepas rawatan"), l3("Consumables", "耗材", "Bahan guna habis"), l3("Injectables", "注射类", "Suntikan")],
    websiteTemplate: "clinic", galleryFilter: "clinic", schemaType: "MedicalClinic",
    bookTitle: l3("Book a consultation", "预约咨询", "Tempah konsultasi"),
    packageExamples: [l3("3 × laser rejuvenation", "激光嫩肤 3 次疗程", "3 × peremajaan laser"), l3("Skin membership (annual)", "年度皮肤管理会员", "Keahlian kulit (tahunan)")],
    whatsappSample: l3("Hi {name}, it's time for your follow-up. Reply to book a consultation with our team.", "{name} 您好，您的复诊时间到了，回复即可预约咨询。", "Hai {name}, sudah tiba masa susulan anda. Balas untuk tempah konsultasi."),
  },
  other: {
    key: "other", version: 1, name: l3("Other / multi-service", "其他·综合", "Lain-lain / pelbagai"), icon: "✨", colors: ["#334155", "#cbd5e1"],
    staffNoun: l3("Staff", "服务人员", "Staf"),
    levels: GENERIC_LEVELS, defaultLevel: "junior", extraRoles: [],
    jobTitles: [l3("Service staff", "服务人员", "Staf servis"), l3("Senior staff", "资深服务人员", "Staf kanan"), l3("Assistant", "助理", "Pembantu"), RECEPTION, MANAGER],
    serviceTemplates: [],
    productCategories: [l3("Retail", "零售", "Runcit"), l3("Consumables", "耗材", "Bahan guna habis"), l3("Tools", "工具", "Peralatan")],
    websiteTemplate: "minimal", galleryFilter: "all", schemaType: "HealthAndBeautyBusiness",
    bookTitle: l3("Book an appointment", "在线预约", "Tempah temujanji"),
    packageExamples: [l3("10-visit card", "10 次卡", "Kad 10 lawatan"), l3("Stored value with bonus", "储值送额", "Nilai tersimpan dengan bonus")],
    whatsappSample: l3("Hi {name}, we miss you! Book your next visit this week and enjoy a little extra.", "{name} 您好，好久不见！本周预约有小惊喜等您。", "Hai {name}, kami rindu anda! Tempah lawatan seterusnya minggu ini untuk hadiah kecil."),
  },
};

/** Valid, de-duplicated business types (primary first). Unknown values are dropped. */
export function normalizeIndustries(x: unknown): Industry[] {
  const arr: unknown[] = Array.isArray(x) ? x : typeof x === "string" ? x.replace(/[{}]/g, "").split(",") : [];
  const out: Industry[] = [];
  for (const v of arr) { const k = typeof v === "string" ? v.trim() : v; if (isIndustry(k) && !out.includes(k)) out.push(k); }
  return out;
}

/** Packs for the types (primary first); the neutral "other" pack when none is chosen. */
export function packsFor(types: readonly string[] | null | undefined): IndustryPack[] {
  const t = normalizeIndustries(types ?? []);
  return t.length ? t.map((k) => INDUSTRY_PACKS[k]) : [INDUSTRY_PACKS.other];
}
export const primaryPack = (types: readonly string[] | null | undefined): IndustryPack => packsFor(types)[0]!;

type Loc = "en" | "zh" | "ms";
const loc = (l: string | null | undefined): Loc => (l === "zh" || l === "ms" ? l : "en");

/** The word for "the person doing the service". Several industries with different nouns → the generic noun (服务人员 / Staff). */
export function staffNounFor(types: readonly string[] | null | undefined): L3 {
  const packs = packsFor(types);
  const first = packs[0]!.staffNoun;
  return packs.every((p) => p.staffNoun.zh === first.zh && p.staffNoun.en === first.en) ? first : INDUSTRY_PACKS.other.staffNoun;
}
export const staffNoun = (types: readonly string[] | null | undefined, locale: string | null | undefined): string => staffNounFor(types)[loc(locale)];

const toEntry = (p: PresetEntry): ListEntry => ({ code: p.code, label: null, names: { ...p.names } });

/** Staff level presets for the types: merged by code in order (primary first), no duplicates. */
export function presetLevels(types: readonly string[] | null | undefined): { levels: ListEntry[]; defaultLevel: string } {
  const packs = packsFor(types);
  const seen = new Set<string>(), levels: ListEntry[] = [];
  for (const p of packs) for (const l of p.levels) if (!seen.has(l.code)) { seen.add(l.code); levels.push(toEntry(l)); }
  return { levels: levels.slice(0, 30), defaultLevel: packs[0]!.defaultLevel };
}

/** Performance role presets: Lead / Assistant / Sales plus the industries' extra roles (no duplicates). */
export function presetRoles(types: readonly string[] | null | undefined) {
  const seen = new Set<string>(), roles: ListEntry[] = [];
  for (const r of [...COMMON_ROLES, ...packsFor(types).flatMap((p) => p.extraRoles)]) if (!seen.has(r.code)) { seen.add(r.code); roles.push(toEntry(r)); }
  return { roles: roles.slice(0, 10), defaultServiceRole: "primary", defaultRetailRole: "seller", defaultTopupRole: "seller", defaultPackageRole: "seller" };
}

/** Starter level preset of one business type ("套用预设"). */
export function levelPresetFor(industry: Industry): ListEntry[] { return INDUSTRY_PACKS[industry].levels.map(toEntry); }
export const presetEntry = toEntry;

/** Merge preset entries into an existing list: keeps every existing entry (and its order) and appends missing codes. */
export function mergeEntries(existing: ListEntry[], add: ListEntry[], max = 30): ListEntry[] {
  const out = [...existing];
  for (const a of add) {
    const i = out.findIndex((x) => x.code === a.code);
    if (i >= 0) {
      // fill in missing localized names on an existing entry, never overwrite the salon's own names
      if (a.names) out[i] = { ...out[i]!, names: { ...a.names, ...(out[i]!.names ?? {}) } };
      continue;
    }
    if (out.length >= max) break;
    out.push(a);
  }
  return out;
}

export function jobTitlesFor(types: readonly string[] | null | undefined, locale: string | null | undefined): string[] {
  const l = loc(locale);
  return [...new Set(packsFor(types).flatMap((p) => p.jobTitles.map((j) => j[l])))];
}

/** Service template categories recommended for the types. */
export function serviceTemplatesFor(types: readonly string[] | null | undefined): TemplateCategory[] {
  return [...new Set(packsFor(types).flatMap((p) => p.serviceTemplates))];
}

export function productCategoriesFor(types: readonly string[] | null | undefined, locale: string | null | undefined): string[] {
  const l = loc(locale);
  return [...new Set(packsFor(types).flatMap((p) => p.productCategories.map((c) => c[l])))];
}

export const defaultWebsiteTemplate = (types: readonly string[] | null | undefined): string => primaryPack(types).websiteTemplate;
export const galleryFilterFor = (types: readonly string[] | null | undefined): string => (normalizeIndustries(types ?? []).length ? primaryPack(types).galleryFilter : "all");
export const schemaTypeFor = (types: readonly string[] | null | undefined): string => primaryPack(types).schemaType;
export const bookTitleFor = (types: readonly string[] | null | undefined, locale: string | null | undefined): string => primaryPack(types).bookTitle[loc(locale)];
export const packageExamplesFor = (types: readonly string[] | null | undefined, locale: string | null | undefined): string[] => [...new Set(packsFor(types).flatMap((p) => p.packageExamples.map((x) => x[loc(locale)])))];
export const whatsappSampleFor = (types: readonly string[] | null | undefined, locale: string | null | undefined): string => primaryPack(types).whatsappSample[loc(locale)];
export const industryName = (k: string, locale: string | null | undefined): string => (isIndustry(k) ? INDUSTRY_PACKS[k].name[loc(locale)] : k);

/** Localized names a code is known by in the presets (for entries saved without names). */
export function knownNames(kind: "level" | "role", code: string): LocalNames | null {
  if (kind === "role") return [...COMMON_ROLES, ...SUGGESTED_ROLES].find((r) => r.code === code)?.names ?? null;
  for (const p of Object.values(INDUSTRY_PACKS)) { const f = p.levels.find((l) => l.code === code); if (f) return f.names; }
  return null;
}
